import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
    LineChart, Line, CartesianGrid, Legend,
} from "recharts";
import { TrendingUp, AlertTriangle, Filter, Gauge, Zap } from "lucide-react";
import { motion } from "framer-motion";

const HORIZONS = [3, 6, 12, 18, 24];

export default function Forecast() {
    const [horizon, setHorizon] = useState(12);
    const [monthly, setMonthly] = useState(null);
    const [funnel, setFunnel] = useState(null);
    const [velocity, setVelocity] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            api.get(`/forecast/monthly?months=${horizon}`),
            api.get(`/forecast/funnel`),
            api.get(`/forecast/velocity`),
        ]).then(([m, f, v]) => {
            setMonthly(m.data); setFunnel(f.data); setVelocity(v.data);
        }).finally(() => setLoading(false));
    }, [horizon]);

    if (loading) return <div className="p-8 text-sm text-muted-foreground">Loading forecast…</div>;

    return (
        <div data-testid="forecast-page">
            <PageHeader title="Pipeline forecast" subtitle="Probability-weighted fees, conversion funnel and stage velocity">
                <Badge className="bg-foreground text-background gap-1.5">
                    <TrendingUp className="w-3 h-3" /> Forecast
                </Badge>
            </PageHeader>

            <div className="p-6 lg:p-8 space-y-6">
                <MonthlyChart data={monthly} horizon={horizon} setHorizon={setHorizon} />
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <FunnelCard data={funnel} />
                    <VelocityCard data={velocity} />
                </div>
            </div>
        </div>
    );
}

function MonthlyChart({ data, horizon, setHorizon }) {
    // Use the backend's own currency determination — never infer it from
    // whichever deal happens to be first in the first bucket. When deals
    // span more than one currency, data.currency is null and
    // data.totals_by_currency carries a real per-currency breakdown instead
    // of a single blended (and financially meaningless) total.
    const mixedCurrencies = !!data?.mixed_currencies;
    const currency = data?.currency || "GBP";
    const buckets = data?.buckets || [];
    const ymax = Math.max(1, ...buckets.map((b) => Math.max(b.gross_fee, b.weighted_fee)));

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
            className="border border-border rounded-md bg-white p-6"
            data-testid="forecast-monthly-card"
        >
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h3 className="font-display font-bold tracking-tight">Weighted fee forecast</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                        Bars = gross fee. Solid overlay = probability-weighted fee.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="text-right">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total weighted</div>
                        <div className="font-mono font-bold text-gold text-lg leading-tight" data-testid="forecast-total-weighted">
                            {mixedCurrencies ? (
                                <span className="text-sm font-normal">Mixed currencies</span>
                            ) : (
                                fmtMoney(data?.total_weighted_fee || 0, currency)
                            )}
                        </div>
                    </div>
                    <Select value={String(horizon)} onValueChange={(v) => setHorizon(parseInt(v))}>
                        <SelectTrigger className="w-32" data-testid="forecast-horizon-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {HORIZONS.map((h) => <SelectItem key={h} value={String(h)}>{h} months</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div className="h-72 -ml-2">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={buckets} margin={{ top: 10, right: 8, left: 12, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                        <XAxis dataKey="month" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
                        <YAxis
                            tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                            tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }}
                            domain={[0, ymax * 1.1]}
                        />
                        <Tooltip
                            formatter={(v, name) => [fmtMoney(v, currency), name === "weighted_fee" ? "Weighted" : "Gross"]}
                            labelStyle={{ fontFamily: "JetBrains Mono", fontSize: 12 }}
                        />
                        <Bar dataKey="gross_fee" fill="rgba(184,148,86,0.25)" name="Gross" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="weighted_fee" fill="rgb(184,148,86)" name="Weighted" radius={[4, 4, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </div>

            {data?.excluded_count > 0 && (
                <div
                    className="mt-4 flex items-start gap-2 px-3 py-2 border border-amber-200 bg-amber-50 rounded-md text-xs"
                    data-testid="forecast-excluded-banner"
                >
                    <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <span className="text-amber-900 font-medium">{data.excluded_count} active deal{data.excluded_count === 1 ? "" : "s"} excluded from forecast</span>
                        <span className="text-amber-700"> — expected close date or fee not set.</span>
                        <Link
                            to="/pipeline?missing=forecast"
                            className="ml-2 inline-flex items-center gap-1 text-amber-900 hover:text-amber-700 underline font-medium"
                            data-testid="forecast-fix-missing-link"
                        >
                            <Filter className="w-3 h-3" /> Fix missing data
                        </Link>
                    </div>
                </div>
            )}
            {data?.out_of_horizon_count > 0 && (
                <div
                    className="mt-2 flex items-start gap-2 px-3 py-2 border border-sky-200 bg-sky-50 rounded-md text-xs"
                    data-testid="forecast-out-of-horizon-banner"
                >
                    <AlertTriangle className="w-4 h-4 text-sky-700 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <span className="text-sky-900 font-medium">{data.out_of_horizon_count} deal{data.out_of_horizon_count === 1 ? "" : "s"} outside horizon</span>
                        <span className="text-sky-700"> — close date is past or beyond {data.horizon_months} months. Extend horizon to include them.</span>
                    </div>
                </div>
            )}
            {mixedCurrencies && (
                <div
                    className="mt-2 flex items-start gap-2 px-3 py-2 border border-amber-200 bg-amber-50 rounded-md text-xs"
                    data-testid="forecast-mixed-currency-banner"
                >
                    <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <span className="text-amber-900 font-medium">
                            Deals in this forecast use {data.currencies.length} different currencies ({data.currencies.join(", ")})
                        </span>
                        <span className="text-amber-700"> — totals aren't summed across currencies. Per-currency totals:</span>
                        <ul className="mt-1 space-y-0.5">
                            {Object.entries(data.totals_by_currency || {}).map(([ccy, t]) => (
                                <li key={ccy} className="font-mono">
                                    {ccy}: {fmtMoney(t.weighted_fee, ccy)} weighted / {fmtMoney(t.gross_fee, ccy)} gross ({t.deal_count} deal{t.deal_count === 1 ? "" : "s"})
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}
        </motion.div>
    );
}

function FunnelCard({ data }) {
    const stages = (data?.stages || []).filter((s) => s.count > 0 && s.name !== "Lost");
    const maxCount = Math.max(1, ...stages.map((s) => s.count));
    return (
        <div className="border border-border rounded-md bg-white p-6" data-testid="forecast-funnel-card">
            <h3 className="font-display font-bold tracking-tight mb-4 flex items-center gap-2">
                <Gauge className="w-4 h-4 text-gold" /> Conversion funnel
            </h3>
            {stages.length === 0 ? (
                <div className="text-sm text-muted-foreground">No active deals.</div>
            ) : (
                <ul className="space-y-1.5">
                    {stages.map((s, i) => {
                        const width = (s.count / maxCount) * 100;
                        const prev = i > 0 ? stages[i - 1].count : null;
                        const conv = prev ? ((s.count / prev) * 100).toFixed(0) : null;
                        return (
                            <li key={s.name} className="group">
                                <div className="flex items-center justify-between mb-0.5">
                                    <span className="text-xs font-medium uppercase tracking-wider text-foreground/80">{s.name}</span>
                                    <span className="font-mono text-xs text-muted-foreground">
                                        {s.count} · {fmtMoney(s.value, "GBP")}
                                        {conv !== null && <span className="ml-2 text-gold">↘ {conv}%</span>}
                                    </span>
                                </div>
                                <div className="h-6 bg-muted/40 rounded-sm overflow-hidden relative">
                                    <motion.div
                                        initial={{ width: 0 }}
                                        animate={{ width: `${width}%` }}
                                        transition={{ duration: 0.4, delay: i * 0.04 }}
                                        className="h-full bg-navy rounded-sm"
                                    />
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

function VelocityCard({ data }) {
    const stages = (data?.stages || []).filter((s) => s.sample_size > 0);
    return (
        <div className="border border-border rounded-md bg-white p-6" data-testid="forecast-velocity-card">
            <h3 className="font-display font-bold tracking-tight mb-4 flex items-center gap-2">
                <Zap className="w-4 h-4 text-gold" /> Stage velocity
            </h3>
            {stages.length === 0 ? (
                <div className="text-sm text-muted-foreground">Not enough stage-change history yet to compute velocity.</div>
            ) : (
                <table className="w-full text-sm" data-testid="velocity-table">
                    <thead>
                        <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                            <th className="text-left py-2">Stage</th>
                            <th className="text-right py-2">Avg days</th>
                            <th className="text-right py-2">Median</th>
                            <th className="text-right py-2">Sample</th>
                        </tr>
                    </thead>
                    <tbody>
                        {stages.map((s) => (
                            <tr key={s.stage} className="border-b border-border/60 last:border-0">
                                <td className="py-2.5 text-xs font-medium">{s.stage}</td>
                                <td className="py-2.5 text-right font-mono text-xs">{s.avg_days}</td>
                                <td className="py-2.5 text-right font-mono text-xs text-muted-foreground">{s.median_days}</td>
                                <td className="py-2.5 text-right font-mono text-[10px] text-muted-foreground">n={s.sample_size}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}
